/**
 * Code 128 barcodes, drawn as SVG, with no dependency.
 *
 * The receipt's serial (QA report §5.1) is printed as a barcode so the desk
 * can scan a patient's receipt instead of typing thirteen digits. Code 128 is
 * what every handheld and every keyboard-wedge scanner reads out of the box,
 * and set C packs digit pairs into one symbol each, so a thirteen-digit serial
 * stays short enough for a narrow thermal receipt.
 *
 * Pure: `encodeCode128` returns the module pattern; `code128Svg` renders it.
 * The encoder picks set C for runs of digits and set B for everything else.
 */

/* The 107 symbol patterns, as bar/space widths (6 digits each, 7 for STOP). */
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

const START_B = 104;
const START_C = 105;
const CODE_B = 100;
const CODE_C = 99;
const STOP = 106;

/** Symbol values for `text`, start and checksum included. Throws on a character set B cannot carry. */
export function encodeCode128(text) {
  const value = String(text ?? '');
  if (!value) throw new Error('Nothing to encode');
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code > 126) throw new Error(`Code 128 set B cannot encode "${ch}"`);
  }

  const codes = [];
  let set = null;
  let i = 0;
  const digitRun = (from) => {
    let n = 0;
    while (from + n < value.length && /\d/.test(value[from + n])) n += 1;
    return n;
  };

  // Set C for an even run of 4+ digits (or a whole all-digit value of 2+).
  const wantC = (from) => {
    const run = digitRun(from);
    return run >= 4 || (run >= 2 && from === 0 && run === value.length);
  };

  while (i < value.length) {
    if (wantC(i)) {
      let run = digitRun(i);
      if (run % 2 === 1) {
        // An odd run: the first digit goes out in set B so the rest pair up.
        if (set !== 'B') {
          codes.push(set === null ? START_B : CODE_B);
          set = 'B';
        }
        codes.push(value.charCodeAt(i) - 32);
        i += 1;
        run -= 1;
      }
      if (set !== 'C') {
        codes.push(set === null ? START_C : CODE_C);
        set = 'C';
      }
      for (let k = 0; k < run; k += 2) {
        codes.push(Number(value.slice(i, i + 2)));
        i += 2;
      }
    } else {
      if (set !== 'B') {
        codes.push(set === null ? START_B : CODE_B);
        set = 'B';
      }
      codes.push(value.charCodeAt(i) - 32);
      i += 1;
    }
  }

  let checksum = codes[0];
  for (let k = 1; k < codes.length; k += 1) checksum += codes[k] * k;
  codes.push(checksum % 103);
  codes.push(STOP);
  return codes;
}

/** The bar/space widths of a whole symbol, quiet zones excluded. */
export function code128Modules(text) {
  return encodeCode128(text)
    .map((code) => PATTERNS[code])
    .join('')
    .split('')
    .map(Number);
}

/**
 * An SVG string of the barcode. Bars are black on white whatever the theme:
 * a scanner needs contrast, not a palette.
 */
export function code128Svg(text, { height = 56, module = 2, quiet = 10 } = {}) {
  const widths = code128Modules(text);
  const total = widths.reduce((a, b) => a + b, 0) + quiet * 2;
  let x = quiet;
  let bars = '';
  widths.forEach((w, index) => {
    if (index % 2 === 0) bars += `<rect x="${x}" y="0" width="${w}" height="${height}"/>`;
    x += w;
  });
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${height}" width="${total * module}" height="${height}" ` +
    `preserveAspectRatio="none" role="img" aria-label="${String(text).replace(/"/g, '')}">` +
    `<rect width="100%" height="100%" fill="#fff"/><g fill="#000">${bars}</g></svg>`
  );
}
